import React from 'react';
import logger from '@hydra/utils/logger';
import { ProjectInterface, ReviewInterface } from '@hydra/types/review';

const FETCH_START = 'FETCH_START';
const FETCH_SUCCESS = 'FETCH_SUCCESS';
const FETCH_FAIL = 'FETCH_FAIL';
const FETCH_FORWARD = 'FETCH_FORWARD';
const FETCH_BACK = 'FETCH_BACK';
const FETCH_FIRST_PAGE = 'FETCH_FIRST_PAGE';

interface State {
  loading: boolean
  rows: any[]
  cursor: number | null
  count: number
  currentPage: number
  hasNext: boolean
  backDisabled: boolean
  nextDisabled: boolean
  hasPrevious: boolean
  error: Error | null
}

type ActionStart = {
  type: 'FETCH_START'
  direction: string
}

type ActionSuccess = {
  type: 'FETCH_SUCCESS',
  direction: string
  rowsPerPage: number
  cursor: number
  count: number
  items: ProjectInterface[] | ReviewInterface[]
}

type ActionFail = {
  type: 'FETCH_FAIL'
  error: any
}

type Action = ActionStart | ActionSuccess | ActionFail

function asyncPaginationReducer (state: State, action: Action) {
  switch (action.type) {
    case FETCH_START: {
      return {
        ...state,
        currentPage: action.direction === FETCH_FIRST_PAGE ? 0 : state.currentPage,
        loading: true,
        backDisabled: true,
        nextDisabled: true,
        error: null
      };
    }
    case FETCH_SUCCESS: {
      const rows = action.items;
      const cursor = action.cursor || state.cursor;
      const count = action.count || state.count || 0;
      let hasNext = state.hasNext;
      let hasPrevious = state.hasPrevious;
      let currentPage = state.currentPage;

      if (action.direction === FETCH_FORWARD) {
        hasNext = !!action.cursor && action.items.length > action.rowsPerPage;
        if (action.cursor) {
          hasPrevious = true;
        }
        currentPage++;
      } else if (action.direction === FETCH_BACK) {
        hasPrevious =
          !!action.cursor && action.items.length > action.rowsPerPage;
        if (action.cursor) {
          hasNext = true;
        }
        currentPage--;
      }
      return {
        ...state,
        loading: false,
        rows,
        cursor,
        count,
        currentPage,
        hasNext,
        backDisabled: currentPage === 0,
        nextDisabled: count !== -1 ? currentPage >= Math.ceil(count / action.rowsPerPage) - 1 : false,
        hasPrevious
      };
    }
    case FETCH_FAIL:
      return { ...state, loading: false, error: action.error };
    default:
      throw new Error();
  }
}

type FetchItems = (qs?: {
  amount?: number;
}) => Promise<ProjectInterface | ReviewInterface>

export interface LabelDisplayedRowsProps {
  from: number
  to: number
  count: number
}

interface Opts {
  rowsPerPage?: number
  rowsPerPageOptions?: number[]
  initialFetchResult?: {
    items?: any[]
    cursor?: number
    count?: number
  }
  onChangeRowsPerPage?: React.Dispatch<number>
  labelDisplayedRows?: (param: LabelDisplayedRowsProps) => React.ReactNode
}

export default function useAsyncPagination (fetchItems: FetchItems, opts: Opts) {
  const {
    rowsPerPage: initialRowsPerPage = 5,
    onChangeRowsPerPage: onChangeRowsPerPageCallback = () => {},
    rowsPerPageOptions = [5],
    initialFetchResult,
    labelDisplayedRows = () => ''
  } = opts;
  const [rowsPerPage, setRowsPerPage] = React.useState(initialRowsPerPage);
  const [state, dispatch] = React.useReducer<React.Reducer<State, Action>>(asyncPaginationReducer, {
    error: null,
    loading: false,
    rows: (initialFetchResult && initialFetchResult.items) || [],
    cursor: (initialFetchResult && initialFetchResult.cursor) || null,
    count: (initialFetchResult && initialFetchResult.count) || 0,
    currentPage: 0,
    backDisabled: true,
    nextDisabled: false,
    hasNext: true,
    hasPrevious: false
  });

  const doFetch = React.useCallback(
    async (opts, direction: string | undefined = FETCH_FIRST_PAGE) => {
      try {
        dispatch({
          type: FETCH_START,
          direction
        });
        const result: any = await fetchItems({
          ...opts,
          amount: rowsPerPage
        });
        dispatch({
          type: FETCH_SUCCESS,
          direction,
          rowsPerPage,
          ...result
        });
      } catch (error) {
        dispatch({ type: FETCH_FAIL, error });
      }
    },
    [fetchItems, rowsPerPage]
  );

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    const forward = newPage > state.currentPage;
    doFetch(
      state.cursor ? (forward ? { after: state.cursor } : { before: state.cursor }) : {},
      forward ? FETCH_FORWARD : FETCH_BACK
    );
  };

  const handleChangeRowsPerPage: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = event => {
    state.currentPage = 0;
    try {
      onChangeRowsPerPageCallback(Number(event.target.value));
      setRowsPerPage(Number(event.target.value));
    } catch (err) {
      logger.error({
        event: 'asyncpagination-onchangerowsperpage-event-error',
        err
      });
    }
  };

  const isFirstRender = React.useRef(true);
  const initialFetchResultRef = React.useRef(initialFetchResult);

  React.useEffect(() => {
    if (!isFirstRender.current || !initialFetchResultRef.current) {
      // ignore first fetch when there is an initalResult
      doFetch({});
    }
    isFirstRender.current = false;
  }, [doFetch]);

  return {
    refresh: doFetch,
    rows: state.rows,
    loading: state.loading,
    isFirstRender: isFirstRender.current,
    error: state.error,
    paginationProps: {
      labelDisplayedRows,
      labelRowsPerPage: '',
      rowsPerPageOptions,
      page: state.currentPage,
      rowsPerPage,
      count: state.count,
      onChangePage: handleChangePage,
      nextIconButtonProps: {
        disabled: state.loading || state.nextDisabled
      },
      backIconButtonProps: {
        disabled: state.loading || state.backDisabled
      },
      onChangeRowsPerPage: handleChangeRowsPerPage
    }
  };
}
